import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { OpenMarketSearchLandingButton } from '../../../components/common/buttons/OpenMarketSearchLandingButton';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { marketPricePageInfo, pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useLayout } from '../../../hooks/screen/useLayout';

import { UseQueryResult, useQueries } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import { ampli } from '../../../ampli';
import { getProducts } from '../../../api/product/queries/getProducts';
import PROMO_GIF_IMAGE from '../../../assets/images/home_market_price_promo.gif';
import { MainHead } from '../../../components/common/head/MainHead';
import { ProductStatusConstant } from '../../../constants/product/product.constant';
import { useGetInformationsQuery } from '../../../hooks/api/information/queries/useGetInformations.query';
import { useGetOpenMarketProductsQuery } from '../../../hooks/api/product/queries/useGetOpenMarketProducts.query';
import { useGetProductsQuery } from '../../../hooks/api/product/queries/useGetProducts.query';
import { useGetNewProductsQuery } from '../../../hooks/api/useGetNewProducts.query';
import { useGetBannersQuery } from '../../../hooks/banners/queries/useGetBanners.query';
import { useGetDownloadAppModalHeight } from '../../../hooks/modal/useGetDownloadAppModalHeight';
import { IProduct } from '../../../interfaces/Product.interfaces';
import { updateManifest } from '../../../utils/updater/updateManifest';
import { InfoHorizontalListDetailViewGroup } from '../../../views/Info/listView/InfoHorizontalListDetailView/InfoHorizontalListDetailViewGroup';
import { OpenMarketQuadSwiperGroup } from '../../../views/Product/detailView/components/OpenMarketQuadSwiperGroup';
import { ProductQuadSwiperGroup } from '../../../views/Product/detailView/components/ProductQuadSwiperGroup';
import { NewProductPreviewCardSwiperWrapper } from '../../../views/Product/listView/NewProductPreviewCard/NewProductPreviewCardSwiperWrapper';
import { DirectMenuSection } from './components/DirectMenuSection';
import { GoToJoinSection } from './components/GoToJoinSection';
import { MainCarousel } from './components/MainCarousel';
import { RecommendedProductsSection } from './components/RecommendedProductsSection';

export const HomePage = () => {
  const [recommendedProducts, setRecommendedProducts] = useState<IProduct[]>([]);

  const navigate = useNavigate();

  const downloadAppModalHeight = useGetDownloadAppModalHeight();

  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: false }} hasPluvaLogo hasMyInfoButton />,
    fixedHeaderHeight: 48,
  });

  const { data: mainCarouselImages, isLoading: isGetMainCarouselImagesQueryLoading } = useGetBannersQuery({
    searchParam: {
      category: 'CAROUSEL_MAIN',
      outputLocation: 'home',
    },
  });

  const { data: pluvaProducts, isLoading: isGetPluvaProductsQueryLoading } = useGetProductsQuery({
    searchParam: {
      category: 'PLUVA',
      status: ProductStatusConstant.ONSALE,
      count: 8,
    },
  });

  const { data: partnerProducts, isLoading: isGetPartnerProductsQueryLoading } = useGetProductsQuery({
    searchParam: {
      category: 'PARTNER',
      status: ProductStatusConstant.ONSALE,
      count: 5,
    },
  });

  const { data: openMarketProducts, isLoading: isGetOpenMarketProductsQueryLoading } = useGetOpenMarketProductsQuery({
    searchParam: {
      status: ProductStatusConstant.ONSALE,
      machineType: 'tractor',
      count: 12,
      page: 1,
    },
  });

  const recommendedOpenMarketProducts =
    openMarketProducts && openMarketProducts.length > 8 ? openMarketProducts.slice(8, 12) : openMarketProducts;

  const { data: informations, isLoading: isGetInformationsQueryLoading } = useGetInformationsQuery({
    searchParam: { category: 'MAGAZINE', count: 5 },
  });

  const { data: recommendedNewProducts, isLoading: isGetNewProductsQueryLoading } = useGetNewProductsQuery({
    options: {
      select: (data) => {
        return data.filter((product) => product.status === 'ONSALE').filter((product) => product.recommendedYn);
      },
    },
  });

  const { data: newProducts } = useGetNewProductsQuery({
    options: {
      select: (data) => {
        return data.filter((product) => product.status === 'ONSALE');
      },
    },
  });

  const allProductsQueries = useQueries({
    queries: [
      {
        queryKey: ['all_partner_products_on_sale'],
        queryFn: () =>
          getProducts({
            searchParam: {
              category: 'PARTNER',
              status: ProductStatusConstant.ONSALE,
            },
          }),
      },
      {
        queryKey: ['all_pluva_products_on_sale'],
        queryFn: () =>
          getProducts({
            searchParam: {
              category: 'PLUVA',
              status: ProductStatusConstant.ONSALE,
            },
          }),
      },
      {
        queryKey: ['all_open_market_products_on_sale'],
        queryFn: () =>
          getProducts({
            searchParam: {
              category: 'OPEN_MARKET',
              status: ProductStatusConstant.ONSALE,
            },
          }),
      },
    ],
  });

  const hasProductsToRecommend =
    (recommendedProducts && recommendedProducts.length > 0) ||
    (recommendedOpenMarketProducts && recommendedOpenMarketProducts.length > 0);

  useEffect(() => {
    const combinedProducts = allProductsQueries.reduce((acc: IProduct[], curr: UseQueryResult<IProduct[]>) => {
      if (!curr.isError && !curr.isLoading && curr.data) {
        const filteredProducts: IProduct[] = curr.data.filter((product: IProduct) => {
          return !!product.recommendedYn;
        });
        return acc.concat(filteredProducts);
      }
      return acc;
    }, []);

    setRecommendedProducts(combinedProducts);
  }, [allProductsQueries[0].data, allProductsQueries[1].data, allProductsQueries[2].data]);

  useEffect(() => {
    const MANIFEST_DEFAULT_NAME = '신뢰할 수 있는 중고 트랙터 플랫폼 | 플루바 마켓';
    updateManifest({ manifest: { name: MANIFEST_DEFAULT_NAME } });
  }, []);

  if (
    isGetMainCarouselImagesQueryLoading ||
    isGetPluvaProductsQueryLoading ||
    isGetPartnerProductsQueryLoading ||
    isGetOpenMarketProductsQueryLoading ||
    isGetInformationsQueryLoading
  )
    return <AnimateLoading />;

  return (
    <>
      <Helmet>
        <title>{pageInfo.homePage.title}</title>
      </Helmet>

      <Container>
        <TopSection>
          {mainCarouselImages && mainCarouselImages.length > 0 && (
            <MainCarousel carouselImages={mainCarouselImages} pageType={'home'} position={'top'} />
          )}
        </TopSection>

        <SearchButtonSection
          style={{
            position: 'sticky',
            top: `${48 + downloadAppModalHeight}px`,
            backgroundColor: 'white',
            zIndex: 56,
          }}
        >
          <OpenMarketSearchLandingButton pageType="홈" />
        </SearchButtonSection>

        <DirectMenuSection />

        {pluvaProducts && pluvaProducts.length >= 4 && (
          <ContentWrapper paddingTop={40}>
            <ContentTitle>{'안심 보증, 프리미엄 인증 트랙터'}</ContentTitle>
            <ProductQuadSwiperGroup
              products={pluvaProducts}
              goToButton={{ renderedIn: 'home', itemType: 'pluva' }}
              pageType={'home'}
              slideHeight={545}
            />
          </ContentWrapper>
        )}

        {openMarketProducts && openMarketProducts.length >= 8 && (
          <ContentWrapper paddingTop={40} paddingBottom={40}>
            <ContentTitle>{'가성비 있는 회원 직거래 매물'}</ContentTitle>
            <OpenMarketQuadSwiperGroup
              pageType={'home'}
              openMarketProducts={openMarketProducts.slice(0, 8)}
              goToButton={{ renderedIn: 'home', itemType: 'open-market' }}
              isInquiryButtonVisible
              slideHeight={545}
            />
          </ContentWrapper>
        )}

        <PromoGIFImageSection
          onClick={() => {
            ampli.pvmClickMainCarousel({
              carousel_title: 'go_to_market_price_main_bottom',
              page_type: 'home',
              position: 'bottom',
            });

            ReactGA.event('pvm_click_main_carousel', {
              action: 'click',
              category: 'carousel',
              carousel_title: 'go_to_market_price_main_bottom',
              page_type: 'home',
              position: 'bottom',
            });
            navigate(marketPricePageInfo.listPage.path);
          }}
        >
          <PromoImageWrapper src={PROMO_GIF_IMAGE} />
        </PromoGIFImageSection>

        {newProducts && newProducts.length >= 3 && (
          <NewProductPreviewCardSwiperWrapper products={newProducts} title={'신품은 어때요?'} />
        )}

        {hasProductsToRecommend && (
          <ContentWrapper paddingBottom={40} paddingTop={40}>
            <RecommendedProductsSection
              products={recommendedProducts}
              openMarketProducts={recommendedOpenMarketProducts}
              newProducts={recommendedNewProducts}
            />
          </ContentWrapper>
        )}

        {informations && (
          <ContentWrapper paddingBottom={40}>
            <ContentTitle>{'읽을수록 도움되는 꿀정보'}</ContentTitle>
            <InfoHorizontalListDetailViewGroup pageType={'home'} informations={informations.slice(0, 5)} />
          </ContentWrapper>
        )}

        <GoToJoinSection />
      </Container>
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  align-self: stretch;

  padding-bottom: 16px;
`;

const SearchButtonSection = styled.div`
  display: flex;
  padding: 0px 16px 8px 16px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const ContentWrapper = styled.div<{ paddingBottom?: number; paddingTop?: number }>`
  width: 100%;

  padding: ${(props) => (props.paddingTop ? `${props.paddingTop}px` : '0px')} 16px
    ${(props) => (props.paddingBottom ? `${props.paddingBottom}px` : '0px')} 16px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  gap: 16px;
`;

const ContentTitle = styled.h3`
  width: 100%;

  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 17px */
`;

const PromoGIFImageSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-black, #232429);
  cursor: pointer;
`;

const PromoImageWrapper = styled.img`
  width: 100%;
  max-height: 520px;
`;
